import { Plugin } from 'prosemirror-state';

/**
 * Checks if text contains Hebrew characters
 * @param {string} text - The text to check
 * @returns {boolean} True if text contains Hebrew characters
 */
function containsHebrew(text) {
  // Hebrew Unicode range: U+0590 to U+05FF
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
}

/**
 * Gets the text alignment based on the content
 * @param {string} text - The text to analyze
 * @returns {string} 'right' for Hebrew text, 'left' for LTR text
 */
function getTextAlignment(text) {
  if (!text || text.trim() === '') {
    return null;
  }
  return containsHebrew(text) ? 'right' : 'left';
}

/**
 * Creates a ProseMirror plugin that automatically sets text-align: right for Hebrew text
 * @returns {Plugin} A ProseMirror plugin that handles automatic text alignment
 */
const autoDirectionPlugin = new Plugin({
  appendTransaction(transactions, oldState, newState) {
    // Only process if there were changes to the document
    const docChanged = transactions.some(tr => tr.docChanged);
    if (!docChanged) {
      return null;
    }

    let tr = newState.tr;
    let modified = false;

    // Iterate through all paragraph nodes
    newState.doc.descendants((node, pos) => {
      if (node.type.name === 'paragraph') {
        // Get the text content of the paragraph
        const textContent = node.textContent;
        const isHebrew = containsHebrew(textContent);
        const detectedDirection = isHebrew ? 'rtl' : 'ltr';
        const currentDirection = node.attrs.direction;

        // Always update direction based on content
        let needsDirectionUpdate = detectedDirection !== currentDirection;

        // Only auto-update align if autoDetected is true
        let needsAlignUpdate = false;
        let detectedAlign = node.attrs.align;

        if (node.attrs.autoDetected !== false) {
          const newAlign = getTextAlignment(textContent);
          if (newAlign && newAlign !== node.attrs.align) {
            detectedAlign = newAlign;
            needsAlignUpdate = true;
          }
        }

        // Update if either direction or align needs updating
        if (needsDirectionUpdate || needsAlignUpdate) {
          tr.setNodeMarkup(pos, null, {
            ...node.attrs,
            align: detectedAlign,
            direction: detectedDirection,
            autoDetected: needsAlignUpdate ? true : node.attrs.autoDetected,
          });
          modified = true;
        }
      }
    });

    return modified ? tr : null;
  },
});

export default autoDirectionPlugin;
